import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const TermsOfService = () => {
  const navigate = useNavigate();

  usePageMeta({
    title: 'Terms of Service - Monthly Wealth Tracker',
    description: 'Read the terms of service for using Monthly Wealth Tracker.',
    path: '/terms',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-slate-900">
      <div className="max-w-4xl mx-auto p-6 sm:p-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 mb-6 font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-lg p-8 sm:p-12">
          <h1 className="text-4xl font-bold text-gray-100 mb-4">Terms of Service</h1>
          <p className="text-gray-400 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">1. Acceptance of Terms</h2>
              <p className="leading-relaxed">
                By accessing or using Monthly Wealth Tracker ("the Service"), you agree to be bound by these Terms of Service ("Terms").
                If you do not agree to these Terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">2. Description of Service</h2>
              <p className="leading-relaxed mb-3">
                Monthly Wealth Tracker is a personal finance tracking application that allows you to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Track your assets and debts over time</li>
                <li>Record monthly financial snapshots</li>
                <li>Visualize your net worth trends</li>
                <li>Store your data securely in the cloud</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">3. User Accounts</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-2">3.1 Account Creation</h3>
                  <p className="leading-relaxed">
                    To use the Service, you must sign in with a valid Google account. You are responsible for maintaining the security
                    of your account credentials and for all activities that occur under your account.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-2">3.2 Account Responsibilities</h3>
                  <p className="leading-relaxed mb-2">You agree to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your account</li>
                    <li>Notify us immediately of any unauthorized access</li>
                    <li>Be responsible for all activities under your account</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">4. Acceptable Use</h2>
              <p className="leading-relaxed mb-3">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Upload malicious code, viruses, or harmful content</li>
                <li>Scrape, copy, or redistribute content from the Service</li>
                <li>Impersonate another person or entity</li>
                <li>Use automated systems to access the Service without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">5. Your Data</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-2">5.1 Data Ownership</h3>
                  <p className="leading-relaxed">
                    You retain all rights to the financial data you enter into the Service. We do not claim ownership of your data.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-2">5.2 Data Accuracy</h3>
                  <p className="leading-relaxed">
                    You are solely responsible for the accuracy and completeness of the data you enter. We do not verify or validate
                    the information you provide.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-200 mb-2">5.3 Data Backup</h3>
                  <p className="leading-relaxed">
                    While we implement backup procedures, you are responsible for maintaining your own backup copies of important data.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">6. Intellectual Property</h2>
              <p className="leading-relaxed">
                The Service, including its design, code, features, and content (excluding user data), is owned by us and protected by
                copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative
                works without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">7. Disclaimer of Warranties</h2>
              <p className="leading-relaxed mb-3">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Warranties of merchantability or fitness for a particular purpose</li>
                <li>Warranties that the Service will be uninterrupted or error-free</li>
                <li>Warranties regarding the accuracy or reliability of any information</li>
              </ul>
              <p className="leading-relaxed mt-4">
                We do not provide financial advice. The Service is a tool for tracking your personal finances only. You should consult
                with qualified financial professionals for financial advice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">8. Limitation of Liability</h2>
              <p className="leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
                <li>Your use or inability to use the Service</li>
                <li>Unauthorized access to or alteration of your data</li>
                <li>Any errors or omissions in the Service</li>
                <li>Any other matter relating to the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">9. Indemnification</h2>
              <p className="leading-relaxed">
                You agree to indemnify, defend, and hold harmless Monthly Wealth Tracker and its officers, directors, employees, and agents
                from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from your
                use of the Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">10. Termination</h2>
              <p className="leading-relaxed mb-3">
                We reserve the right to suspend or terminate your access to the Service at any time, with or without notice, for any reason,
                including if you violate these Terms. You may terminate your account at any time by deleting your data and discontinuing use of the Service.
              </p>
              <p className="leading-relaxed">
                Upon termination, your right to use the Service will immediately cease. You may request deletion of your data as outlined in our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">11. Changes to the Service</h2>
              <p className="leading-relaxed">
                We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time with or without notice.
                We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">12. Changes to Terms</h2>
              <p className="leading-relaxed">
                We may update these Terms from time to time. The updated Terms will be posted on this page with a revised "Last updated" date.
                Your continued use of the Service after any changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">13. Governing Law</h2>
              <p className="leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which we operate,
                without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">14. Contact Information</h2>
              <p className="leading-relaxed mb-3">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-4">
                <p className="font-medium">Email: <a href="mailto:support@monthlywealthtracker.com" className="text-indigo-400 hover:underline">support@monthlywealthtracker.com</a></p>
              </div>
            </section>

            <section className="border-t border-gray-700 pt-8 mt-8">
              <p className="text-sm text-gray-400">
                By using Monthly Wealth Tracker, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
